import { Autocomplete, Chip, Grid, TextField } from "@mui/material";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { buscarTodosClientes } from "../../services/api-empresas.service";

const normalizeDigits = (s) => String(s ?? "").replace(/\D/g, "");

const getEmpresasFromResponse = (response) => {
  if (Array.isArray(response?.empresas)) return response.empresas;
  if (Array.isArray(response?.empresa)) return response.empresa;
  if (Array.isArray(response?.dados)) return response.dados;
  return [];
};

const mapEmpresasResponse = (response) => {
  const seen = new Set();
  const itens = [];

  for (const emp of getEmpresasFromResponse(response)) {
    const cnpj = normalizeDigits(emp.cnpj);
    if (!cnpj || cnpj.length !== 14 || seen.has(cnpj)) continue;
    if (emp.ativo === false) continue;
    seen.add(cnpj);

    const idEmpresa =
      emp.id_empresa != null ? String(emp.id_empresa) : null;

    const razaoSocial = String(emp.razao_social ?? "").trim();
    const label = `${cnpj}-${razaoSocial}`;
    const codGrupo =
      emp.id_grupo_economico != null && emp.id_grupo_economico !== ""
        ? String(emp.id_grupo_economico)
        : null;

    itens.push({
      id: cnpj,
      idEmpresa,
      optionKey: `${cnpj}-${codGrupo ?? "0"}-${label}`,
      label,
      codGrupo,
      nomeGrupo: emp.nome_grupo_economico ?? null,
    });
  }

  return itens.sort((a, b) => a.label.localeCompare(b.label, "pt-BR"));
};

/**
 * Seleção de empresa por CNPJ ou razão social (api-empresas).
 * Modo único: emite { CNPJ, idEmpresa, nome_CNPJ, GE, nome_GE } — usado nas telas de conciliação bancária.
 * Modo múltiplo: emite string[] com CNPJs (somente dígitos).
 */
const AutoCompleteEmpresaCnpj = ({
  onChange,
  multiple = false,
  value: valueProp,
  empresaInicial,
  label,
  helperText,
  error = false,
  variant = "standard",
  size = "small",
  fullWidth = true,
  embedded = false,
  disablePortal = false,
}) => {
  const noOptionText = "Sem opções";
  const popperProps = disablePortal ? { disablePortal: true } : undefined;

  const [options, setOptions] = useState([]);
  const [value, setValue] = useState(null);
  const empresaInicialChaveRef = useRef(null);
  const aplicouEmpresaInicialRef = useRef(false);

  const formatLabel = (input, textForm = false) => {
    if (!input) return "";

    const [firstHalf, secondHalf] = input.split("-", 2);

    let formattedFirstHalf;
    if (firstHalf.length === 11) {
      formattedFirstHalf = firstHalf.replace(
        /(\d{3})(\d{3})(\d{3})(\d{2})/,
        "$1.$2.$3-$4",
      );
    } else if (firstHalf.length === 14) {
      formattedFirstHalf = firstHalf.replace(
        /(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/,
        "$1.$2.$3/$4-$5",
      );
    } else {
      formattedFirstHalf = firstHalf;
    }

    if (textForm) {
      return `${formattedFirstHalf} - ${secondHalf ?? ""}`;
    }
    return (
      <div style={{ display: "flex", flexDirection: "column" }}>
        <span style={{ fontWeight: "bold" }}>{formattedFirstHalf}</span>
        <span style={{ fontSize: "90%" }}>{secondHalf}</span>
      </div>
    );
  };

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      try {
        const response = await buscarTodosClientes({
          limit: 100000,
          offset: 0,
        });
        if (!cancelled) {
          setOptions(mapEmpresasResponse(response));
        }
      } catch (err) {
        const errorMessage = err.response
          ? err.response.mensagem
          : "Erro: Tente novamente!";
        if (!cancelled && typeof console !== "undefined") {
          console.error("[AutoCompleteEmpresaCnpj]", errorMessage);
        }
      }
    };
    fetchData();
    return () => {
      cancelled = true;
    };
  }, []);

  const filterOptions = (opts, { inputValue }) => {
    const raw = String(inputValue ?? "").trim();
    if (!raw) return opts;

    const qLower = raw.toLowerCase();
    const qDigits = normalizeDigits(raw);

    return opts.filter((opt) => {
      const labelText = opt.label || "";
      const [cnpjPart, ...nomeParts] = labelText.split("-");
      const razao = nomeParts.join("-").toLowerCase();
      const cnpjDigits = normalizeDigits(cnpjPart);

      if (qDigits.length > 0 && cnpjDigits.includes(qDigits)) return true;
      if (razao.includes(qLower)) return true;
      return labelText.toLowerCase().includes(qLower);
    });
  };

  const emitChangeSingle = (selected) => {
    if (!onChange) return;
    if (!selected) {
      onChange({
        CNPJ: null,
        idEmpresa: null,
        nome_CNPJ: null,
        GE: null,
        nome_GE: null,
      });
      return;
    }
    onChange({
      CNPJ: selected.id,
      idEmpresa: selected.idEmpresa ?? null,
      nome_CNPJ: selected.label,
      GE: selected.codGrupo,
      nome_GE: selected.nomeGrupo ?? null,
    });
  };

  const handleChangeSingle = (_event, selected) => {
    setValue(selected);
    emitChangeSingle(selected);
  };

  useEffect(() => {
    const chave = empresaInicial
      ? `${empresaInicial.idEmpresa ?? ""}:${normalizeDigits(empresaInicial.cnpj)}`
      : null;
    if (chave !== empresaInicialChaveRef.current) {
      empresaInicialChaveRef.current = chave;
      aplicouEmpresaInicialRef.current = false;
    }
  }, [empresaInicial]);

  useEffect(() => {
    if (
      multiple ||
      !empresaInicial ||
      !options.length ||
      aplicouEmpresaInicialRef.current
    ) {
      return;
    }
    const cnpjDigits = normalizeDigits(empresaInicial.cnpj);
    const idEmp =
      empresaInicial.idEmpresa != null
        ? String(empresaInicial.idEmpresa)
        : null;
    const match = options.find((opt) => {
      if (cnpjDigits && normalizeDigits(opt.id) === cnpjDigits) return true;
      if (idEmp && String(opt.idEmpresa) === idEmp) return true;
      return false;
    });
    if (match) {
      aplicouEmpresaInicialRef.current = true;
      setValue(match);
      emitChangeSingle(match);
    }
  }, [empresaInicial, multiple, options]);

  const cnpjIdsControlled = useMemo(() => {
    if (!multiple) return [];
    if (!Array.isArray(valueProp)) return [];
    return valueProp.map(normalizeDigits).filter((d) => d.length === 14);
  }, [multiple, valueProp]);

  const selectedMultiple = useMemo(
    () => options.filter((opt) => cnpjIdsControlled.includes(normalizeDigits(opt.id))),
    [options, cnpjIdsControlled],
  );

  const handleChangeMultiple = (_event, selected) => {
    const ids = [...new Set((selected ?? []).map((opt) => normalizeDigits(opt.id)).filter((d) => d.length === 14))];
    onChange?.(ids);
  };

  const defaultLabel = multiple
    ? "Empresas (CNPJ ou nome)"
    : "Empresa (CNPJ ou nome)";

  const autocomplete = multiple ? (
    <Autocomplete
      multiple
      fullWidth={fullWidth}
      size={size}
      options={options}
      componentsProps={popperProps ? { popper: popperProps } : undefined}
      value={selectedMultiple}
      onChange={handleChangeMultiple}
      filterOptions={filterOptions}
      noOptionsText={noOptionText}
      disableCloseOnSelect
      isOptionEqualToValue={(option, v) => option.id === v.id}
      getOptionKey={(option) => option.optionKey ?? option.id}
      getOptionLabel={(option) => formatLabel(option.label, true)}
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option, index) => (
          <Chip
            {...getTagProps({ index })}
            key={option.optionKey ?? option.id}
            label={formatLabel(option.label, true)}
            size="small"
          />
        ))
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={label ?? defaultLabel}
          helperText={helperText}
          error={error}
          variant={variant}
          placeholder="Busque por CNPJ ou razão social"
        />
      )}
      renderOption={(props, option) => {
        const { key, ...optionProps } = props;
        return (
          <li key={key} {...optionProps} style={{ textWrap: "wrap" }}>
            {formatLabel(option.label)}
          </li>
        );
      }}
    />
  ) : (
    <Autocomplete
      fullWidth={fullWidth}
      size={size}
      options={options}
      componentsProps={popperProps ? { popper: popperProps } : undefined}
      value={value}
      onChange={handleChangeSingle}
      filterOptions={filterOptions}
      noOptionsText={noOptionText}
      isOptionEqualToValue={(option, v) => option.id === v.id}
      getOptionKey={(option) => option.optionKey ?? option.id}
      getOptionLabel={(option) => formatLabel(option.label, true)}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label ?? defaultLabel}
          helperText={helperText}
          error={error}
          variant={variant}
        />
      )}
      renderOption={(props, option) => {
        const { key, ...optionProps } = props;
        return (
          <li key={key} {...optionProps} style={{ textWrap: "wrap" }}>
            {formatLabel(option.label)}
          </li>
        );
      }}
    />
  );

  if (embedded) {
    return autocomplete;
  }

  return (
    <Grid container spacing={1}>
      <Grid item xs={12}>
        {autocomplete}
      </Grid>
    </Grid>
  );
};

export default AutoCompleteEmpresaCnpj;
